/*
 * Wmb, Worldline Merchant Billing.
 * Copyright (C) 2017-2022 Worldline
 * All Rights Reserved
 * This product is protected by copyright. Any copying or distribution
 * is forbidden without the agreement of Worldline.
 */
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AbstractMerchantSupplierComponent } from 'app/core/common/abstract.merchant-supplier.component';
import { Merchant } from 'app/core/models/merchant';
import { ContractService } from 'app/core/services/contract.service';
import { MerchantService } from 'app/core/services/merchant.service';
import { NotifMessageService } from 'app/core/services/notifmessage.service';
import { PosService } from 'app/core/services/pos.service';
import { SupplierService } from 'app/core/services/supplier.service';
import { NGXLogger } from 'ngx-logger';
import { forkJoin, ReplaySubject, Subscription, timer } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AuthenticatedRouteGuard } from 'security/authenticatedRouteGuard.service';
import { KeycloakService } from 'security/keycloak.service';
import { tableEventChange } from '../table-utils/classes/tableChangeEvent';
import { actionType } from '../table-utils/enums/actionType';
import { columnType } from '../table-utils/enums/columnType';


@Component({
  selector: 'app-merchant-page',
  templateUrl: './merchant-page.component.html'
})
export class MerchantPageComponent extends AbstractMerchantSupplierComponent implements OnInit {

  dataSubject$: ReplaySubject<any[]> = new ReplaySubject<any[]>();
  totalLength$: ReplaySubject<number> = new ReplaySubject<number>();

  isLoadingResults : boolean = true;
  isDeletedIncluded : boolean = false;

  statusItems: Array<String> = new Array<String>();

  columns: {
    data : {
      title: string;
      type: columnType
    }[],
    clickable : boolean;
    };

  pager : {page : number,
    sort : string,
    size : number
  } = {
    page: 0,
    sort: "createdDate,DESC",
    size: 20
  };

  criteria : Merchant = new Merchant();

  columnPropertyMapping = {
    "MERCHANT_NAME" : "name",
    "EXTERNAL_REF" : "externalRef",
    "CREATION_DATE" : "createdDate",
    "STATUS" : "deleted",
    "ACTIONS" : "actions",
  };

  searchForm : FormGroup = new FormGroup({
    name: new FormControl(),
    id: new FormControl(),
    externalRef: new FormControl(),
    deleted: new FormControl()
  });

  modalResponseSubscription: Subscription;
  statusSubscription: Subscription;

  constructor(
    private translateService: TranslateService,
    private roleGuard: AuthenticatedRouteGuard,
    private notifService: NotifMessageService,
    private keycloakService: KeycloakService,
    protected supplierService: SupplierService,
    protected merchantService: MerchantService,
    protected posService: PosService,
    protected log: NGXLogger,
    private contractService: ContractService,
    public router: Router
  ) { super(supplierService, merchantService, posService, log, router); }

  ngOnInit() {
    this.columns = {
      data : [
      { title: "MERCHANT_NAME", type: columnType.text },
      { title: "EXTERNAL_REF", type: columnType.text },
      { title: "CREATION_DATE", type: columnType.date },
      { title: "STATUS", type: columnType.chip },
      { title: "ACTIONS", type: columnType.actions }
    ],
    clickable : true
    };

    this.statusItems = this.getMerchantSupplierStatus();
    this.criteria["deleted"] = 'false';
    this.keycloakService.rolesObservable.subscribe(roles => {
      if (roles.length !== 0) {
        if (this.roleGuard.isEnabled('MERCHANT_READ')) {
          this.loadData();
        }
      }
    });

    this.statusSubscription = this.searchForm.get("deleted").valueChanges.subscribe(val => {
      if (val === "ALL" || val === null){
      }
      else if (val ==="DISABLED"){
        this.criteria["deleted"] = 'true';
      }
      else if (val === "ACTIVE"){
        this.criteria["deleted"]= 'false';
      }
    });

    this.search();
  }

  ngOnDestroy() {
    super.closeSubscribtions();
  }

  formatTransaction(merchant : Merchant) {
    const actions = [];
    if (this.roleGuard.isEnabled('MERCHANT_EDIT') && !merchant.deleted) {
      actions.push({
        "name" : "EDIT",
        "type" : actionType.navigation,
        "value": `/service/merchant/edit/${merchant.id}`
      });
    }
    if (this.roleGuard.isEnabled('MERCHANT_READ')) {
      actions.push({
        "name" : "DETAILS",
        "type" : actionType.navigation,
        "value": `/service/merchant/${merchant.id}`
      });
    }
    if (this.roleGuard.isEnabled('MERCHANT_DELETE') && !merchant.deleted) {
      actions.push({
        "name" : "DELETE",
        "type" : actionType.eventEmitter,
        "value": merchant.id
      })
    }

    return {
      id: merchant.id,
      MERCHANT_NAME: merchant.name,
      EXTERNAL_REF: merchant.externalRef,
      CREATION_DATE: merchant.createdDate,
      STATUS: merchant.deleted ? "DISABLED" : "ENABLED" ,
      deleted: merchant.deleted,
      ACTIONS: actions
    }
  }

  loadData(isLoadingSearch : boolean = false) : void {
    this.isLoadingResults = true;
    this.merchantService
    .findByCriteria(this.criteria, this.pager, false)
    .pipe(
      tap(merchants => this.totalLength$.next(merchants.totalElements)),
      map(merchants => {
        if (!merchants) {
          this.isLoadingResults = false;
          return [];
        }
        if (merchants.content.length === 0) {
          this.isLoadingResults = false;
          if (isLoadingSearch) {
            this.notifService.notifyInfo('EMPTY_RESULT');
          }
          return [];
        }
        if (merchants.content.length !== 0) {
          this.isLoadingResults = false;
          return merchants.content.map(merchant =>this.formatTransaction(merchant));
        }
      }),
    ).subscribe(merchants =>  {
      this.dataSubject$.next(merchants);
    })
  }

  deleteMerchant(id : number) {
    this.merchantService.getMerchantById(id).subscribe(merchant => {
      this.notifService.emitModalWithDetails("DELETE_MERCHANT", [merchant.name,"MERCHANT_DELETION","DELETE_MERCHANT_MSG","DELETE_FIDELITY_PROGRAM_SUPPLIER"]);
      this.modalResponseSubscription = this.notifService.modalResponseSubject$.subscribe(resp => {
        if (resp) {
          forkJoin(
            this.contractService.deleteContractByMerchantId(id),
            this.merchantService.deleteMerchant(id)
          )
            .subscribe(
              () => {
                this.loadData();
                this.notifService.notifySuccess('MERCHANT_DELETED');
                this.modalResponseSubscription.unsubscribe();
              }
            );
        } else {
          this.modalResponseSubscription.unsubscribe();
        }
      });
    });
  }

  includeDeleted(event : MatCheckboxChange) : void {
    this.isDeletedIncluded = event.checked;
    this.loadData();
  }

  tableChange(event: tableEventChange) {
    const sortProperty = this.columnPropertyMapping[event.sortEvent.active];
    const sortDirection = event.sortEvent.direction.toUpperCase();
    this.pager = {
      page: event.pageIndex,
      sort: sortDirection !== "" && sortProperty ? [sortProperty, sortDirection].join(",") : "lastModifiedDate,DESC",
      size: event.pageSize
    }
    this.loadData();
  }

  search(): void {
    this.criteria = new Merchant();
    for (let field in this.searchForm.value) {
      if (!this.searchForm.value[field]) {
        delete this.searchForm.value[field];
      } else if (field != 'deleted') {
        this.criteria[field] = this.searchForm.value[field];
      }
    }
    this.loadData(true);
  }

  details(id : number) : void{
    this.router.navigate([`/service/merchant/${id}`]);
  }

  clearSearch(): void {
    this.searchForm.reset();
    this.criteria = new Merchant();
    this.loadData();
  }
}
